/**
 * HubSection — a tool section on the Olive hub board (X.5.1; restyled
 * X.6.3 to the design-refresh "Warm" prototype).
 *
 * Header row: the section icon (Lucide) tinted with the section's identity
 * `accent`, a Playfair label, and a gold-to-edge hairline. Below it, the
 * section's visible tiles in an auto-fill grid. The old tinted section card is
 * gone — sections now stack in the Home left column. Everything is x.6.1
 * tokens except the per-section `accent` (section identity, kept as data).
 */

import { type ReactNode } from 'react';
import { Heart, Users, Folder, Settings, Mail, Terminal, type LucideIcon } from 'lucide-react';
import { HubTile } from './HubTile';
import type { HubSection as HubSectionDef, HubSectionIcon, HubTile as HubTileDef } from '@/lib/hubTiles';

// 'settings' (Administrative Tools — Ramp/Rippling/Bill/QBO/Chase) and
// 'terminal' (System Tools — GitHub/Azure) are deliberately distinct icons:
// the two sections read close in label ("Administrative" vs "System") and
// sharing a gear would blur them at a glance.
const SECTION_ICONS: Record<HubSectionIcon, LucideIcon> = {
  heart: Heart,
  users: Users,
  folder: Folder,
  settings: Settings,
  mail: Mail,
  terminal: Terminal,
};

export interface HubSectionProps {
  section: HubSectionDef;
  visibleTiles: readonly HubTileDef[];
  slug: string;
  /** Record a tile open for the Home "Jump back in" row (X.6.3). */
  onTileActivate?: (id: string) => void;
}

export function HubSection({ section, visibleTiles, slug, onTileActivate }: HubSectionProps): ReactNode {
  if (visibleTiles.length === 0) return null;
  const Icon = SECTION_ICONS[section.icon];

  return (
    <section data-testid={`hub-section-${section.id}`}>
      <div className="mb-4 flex items-baseline gap-3.5">
        {/* The label keeps its line when it fits and wraps when it does
            not; the rule beside it gives way first. */}
        <span className="inline-flex min-w-0 items-center gap-2">
          <Icon size={18} aria-hidden="true" className="shrink-0 translate-y-0.5" style={{ color: section.accent }} />
          <span className="min-w-0 break-words font-display text-[19px] font-semibold tracking-[-0.01em] text-sage-800">
            {section.label}
          </span>
        </span>
        <span className="h-px flex-1 bg-gradient-to-r from-warm-400/30 to-transparent" />
      </div>
      <div
        className="grid gap-3.5"
        style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(min(184px, 100%), 1fr))' }}
      >
        {visibleTiles.map((tile) => (
          <HubTile
            key={tile.id}
            tile={tile}
            slug={slug}
            onActivate={onTileActivate ? () => onTileActivate(tile.id) : undefined}
          />
        ))}
      </div>
    </section>
  );
}
